/** Minimum width for a metric column before it flexes to fill space. */
export const METRIC_COLUMN_MIN_WIDTH = 96

/**
 * Width for the tags column when no row has any tags: just enough for the "Tags"
 * header and the lone "+" add button (right-aligned) plus the cell's `px-3`
 * padding — the column shouldn't reserve room for chips that aren't there.
 */
export const TAGS_COLUMN_EMPTY_WIDTH = 64

/**
 * Width for the tags column once any row has tags. Tags render as a single
 * collapsed color-dot chip (up to `MAX_OVERFLOW_DOTS` dots plus a "+N" counter,
 * never label text), so the content width is effectively constant regardless of
 * how many tags a row has: 75px for the widest chip the column can hold (three
 * glyphs and a two-digit counter) plus the cell's 22px of `px-3` padding, and a
 * few px of slack. Anything wider is dead space, since the chip is
 * right-aligned.
 */
export const TAGS_COLUMN_DOTS_WIDTH = 100

/** Fixed pixel widths for known (non-metric) columns, keyed by column key. */
export const COLUMN_WIDTHS: Record<string, number> = {
  status: 40,
  id: 56,
  simulationId: 96,
  runId: 96,
  callSid: 104,
  runName: 200,
  agentName: 200,
  testSetName: 200,
  personaId: 200,
  modelType: 75,
  numSimulations: 80,
  metrics: 200,
  createdBy: 140,
  created: 120,
  default: 100,
}

/** True when a column renders a metric value (key prefixed with `metric:`). */
export function isMetricColumn(key: string): boolean {
  return key.startsWith("metric:")
}

/**
 * Largest number of tags present across the given tag arrays. Used to size the
 * tags column so it can fit the widest row's tag chips.
 */
export function computeMaxTagCount(
  tagArrays: ReadonlyArray<readonly string[] | null | undefined>,
): number {
  return tagArrays.reduce<number>(
    (max, tags) => Math.max(max, tags?.length ?? 0),
    0,
  )
}

/**
 * Pixel width for the tags column. Two states only: the narrow empty width when
 * no row has tags, else the fixed dots-chip width (tags collapse to color dots,
 * so the width no longer scales with the tag count).
 */
export function computeTagsColumnWidth(maxTagCount: number): number {
  return maxTagCount === 0 ? TAGS_COLUMN_EMPTY_WIDTH : TAGS_COLUMN_DOTS_WIDTH
}
